import { createAction, createAsyncThunk, createReducer } from '@reduxjs/toolkit';
import { API, SCOPE_CRM_COMPANIES } from '../../constants';
import { application } from '../../services/application';
import { ANY_OF_THOSE } from '../../constants/filter';
import { INITIAL_LIST_STATE_EXTENDED } from '../helpers/common';
import { extendListBuilder, generateListActions } from '../helpers/listActionsHelpers';

const FIELD_FILTER_KEYS = ['status', 'companyType', 'createdAt'];

export const INITIAL_STATE = {
  ...INITIAL_LIST_STATE_EXTENDED,
  order: ['name', 'asc'],
  pagination: { page: 1, perPage: 20 },
  filters: {
    text: '',
    fields: {
      status: { data: ['active'], type: ANY_OF_THOSE },
    },
  },
  selectedCompany: null,
  companyPending: false,
  savePending: false,
};

const listActions = generateListActions({
  scope: SCOPE_CRM_COMPANIES,
  apiMethod: {
    GET_LIST: API.CRM_COMPANIES.GET_LIST,
  },
  getStore: (store) => store.crmCompanies,
});

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const normalizeFieldFilter = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value?.data && value?.type) {
    return value;
  }

  if (value?.type && (value.from || value.to || (Array.isArray(value.value) && value.value.length))) {
    return value;
  }

  if (Array.isArray(value)) {
    return { data: value, type: ANY_OF_THOSE };
  }

  return { data: [value], type: ANY_OF_THOSE };
};

const getNextOrder = (currentOrder = [], payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (payload?.orderBy) {
    return [payload.orderBy, payload.orderDir || payload.direction || 'asc'];
  }

  if (payload?.field) {
    return [payload.field, payload.direction || 'asc'];
  }

  const [currentOrderBy, currentOrderDir] = currentOrder;
  const nextOrderBy = payload;
  const nextOrderDir = currentOrderBy === nextOrderBy && currentOrderDir === 'asc' ? 'desc' : 'asc';

  return [nextOrderBy, nextOrderDir];
};

export const clearSelectedCompany = createAction(`${SCOPE_CRM_COMPANIES}/clearSelectedCompany`);

export const fetchCompany = createAsyncThunk(`${SCOPE_CRM_COMPANIES}/fetchCompany`, (params) => {
  return application.call(API.CRM_COMPANIES.FIND_ONE, params);
});

export const updateCompany = createAsyncThunk(`${SCOPE_CRM_COMPANIES}/updateCompany`, (params) => {
  return application.call(API.CRM_COMPANIES.UPDATE, params);
});

export const setCompanyListFilters = createAction(`${SCOPE_CRM_COMPANIES}/setCompanyListFilters`);
export const setCompanyListOrder = createAction(`${SCOPE_CRM_COMPANIES}/setCompanyListOrder`);
export const setCompanyListPage = createAction(`${SCOPE_CRM_COMPANIES}/setCompanyListPage`);
export const setCompanyListPerPage = createAction(`${SCOPE_CRM_COMPANIES}/setCompanyListPerPage`);

export const actions = {
  ...listActions,
  clearSelectedCompany,
  fetchCompany,
  updateCompany,
  setCompanyListFilters,
  setCompanyListOrder,
  setCompanyListPage,
  setCompanyListPerPage,
};

export default createReducer(INITIAL_STATE, (builder) => {
  extendListBuilder(builder, actions);

  builder
    .addCase(setCompanyListFilters, (state, action) => {
      const payload = action.payload || {};
      const { fields = {}, text, reset, ...directFilters } = payload;
      const nextFields = { ...state.filters.fields };

      if (hasOwn(payload, 'text')) {
        state.filters.text = text || '';
      }

      FIELD_FILTER_KEYS.forEach((key) => {
        const hasDirectFilter = hasOwn(directFilters, key);
        const hasFieldFilter = hasOwn(fields, key);

        if (!hasDirectFilter && !hasFieldFilter) {
          return;
        }

        const value = hasDirectFilter ? directFilters[key] : fields[key];
        const normalizedValue = normalizeFieldFilter(value);

        if (!normalizedValue) {
          delete nextFields[key];
          return;
        }

        nextFields[key] = normalizedValue;
      });

      state.filters.fields = nextFields;

      if (reset && state.pagination) {
        state.pagination.page = 1;
      }
    })
    .addCase(setCompanyListOrder, (state, action) => {
      state.order = getNextOrder(state.order, action.payload);

      if (state.pagination) {
        state.pagination.page = 1;
      }
    })
    .addCase(setCompanyListPage, (state, action) => {
      if (state.pagination) {
        state.pagination.page = action.payload;
      }
    })
    .addCase(setCompanyListPerPage, (state, action) => {
      if (state.pagination) {
        state.pagination.perPage = action.payload;
        state.pagination.page = 1;
      }
    })
    .addCase(fetchCompany.pending, (state) => {
      state.companyPending = true;
    })
    .addCase(fetchCompany.fulfilled, (state, action) => {
      state.selectedCompany = action.payload;
      state.companyPending = false;
    })
    .addCase(fetchCompany.rejected, (state) => {
      state.selectedCompany = null;
      state.companyPending = false;
    })
    .addCase(updateCompany.pending, (state) => {
      state.savePending = true;
    })
    .addCase(updateCompany.fulfilled, (state, action) => {
      state.savePending = false;
      const company = action.payload?.company;

      if (company) {
        state.selectedCompany = company;
        const index = state.items.findIndex((item) => item.id === company.id);
        if (index >= 0) {
          state.items[index] = company;
        }
      }
    })
    .addCase(updateCompany.rejected, (state) => {
      state.savePending = false;
    })
    .addCase(clearSelectedCompany, (state) => {
      state.selectedCompany = null;
      state.companyPending = false;
      state.savePending = false;
    });

  return builder;
});
